import { MouseEvent, useEffect, useState } from "react";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  ArrowBackIosNewRounded,
  ArrowForwardIosRounded,
  CheckRounded,
  ContentCopyRounded,
  EditRounded,
  ThumbDownAlt,
  ThumbDownAltOutlined,
  ThumbUpAlt,
  ThumbUpAltOutlined,
} from "@mui/icons-material";
import aiChatMessageStyles from "./aiChatMessageStyles";
import { AiChatMessage as AiChatMessageType } from "../../types/AIChat/AiChatMessage";
import { useAiChat } from "../../provider/AiChatProvider";
import dayjs from "dayjs";

type AiChatMessageActionsProps = {
  message: AiChatMessageType;
  variants: AiChatMessageType[];
  onSelectVariant?: (message: AiChatMessageType) => Promise<void>;
  onStartEdit: () => void;
  isInteractionDisabled: boolean;
  isCopyActionPinned: boolean;
};

const feedbackOptions = [
  { rating: "positive", selectedIcon: ThumbUpAlt, unselectedIcon: ThumbUpAltOutlined },
  { rating: "negative", selectedIcon: ThumbDownAlt, unselectedIcon: ThumbDownAltOutlined },
] as const;

export default function AiChatMessageActions({
  message,
  variants,
  onSelectVariant,
  onStartEdit,
  isInteractionDisabled,
  isCopyActionPinned,
}: AiChatMessageActionsProps) {
  const { openFeedback } = useAiChat();
  const [isCopied, setIsCopied] = useState(false);
  const showCopyAction = message.role === "assistant" || isCopyActionPinned || isCopied;
  const canEdit = message.role === "user" && message.forkCheckpointId !== null;
  const variantIndex = variants.findIndex((variant) => variant.uuid === message.uuid);

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const resetCopiedState = () => setIsCopied(false);
    document.addEventListener("click", resetCopiedState, { capture: true, once: true });

    return () => document.removeEventListener("click", resetCopiedState, true);
  }, [isCopied]);

  const copyMessage = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await navigator.clipboard.writeText(message.content);
    setIsCopied(true);
  };

  const selectVariant = async (nextVariantIndex: number) => {
    const nextVariant = variants[nextVariantIndex];
    if (!onSelectVariant || !nextVariant || isInteractionDisabled) {
      return;
    }

    await onSelectVariant(nextVariant);
  };

  return (
    <Stack direction={"row"} spacing={0.5} sx={aiChatMessageStyles.messageActions}>
      <Tooltip title={"Nachricht kopieren"} disableTouchListener>
        <IconButton
          className={"AiChatMessage-copyAction AiChatMessage-hoverAction"}
          size={"small"}
          onClick={copyMessage}
          sx={showCopyAction ? aiChatMessageStyles.copyActionVisible : aiChatMessageStyles.copyAction}
        >
          {isCopied ? <CheckRounded /> : <ContentCopyRounded />}
        </IconButton>
      </Tooltip>
      {canEdit && (
        <Tooltip title={"Nachricht bearbeiten"} disableTouchListener>
          <IconButton
            className={"AiChatMessage-hoverAction"}
            size={"small"}
            disabled={isInteractionDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onStartEdit();
            }}
            sx={aiChatMessageStyles.copyAction}
          >
            <EditRounded />
          </IconButton>
        </Tooltip>
      )}
      {message.role === "assistant" &&
        feedbackOptions.map(({ rating, selectedIcon: SelectedIcon, unselectedIcon: UnselectedIcon }) => {
          const isSelected = message.feedbackRating === rating;
          const Icon = isSelected ? SelectedIcon : UnselectedIcon;

          return (
            <IconButton key={rating} size={"small"} onClick={() => openFeedback(message, rating)}>
              <Icon
                color={isSelected ? "primary" : "inherit"}
                sx={isSelected ? {} : aiChatMessageStyles.feedbackIcon}
              />
            </IconButton>
          );
        })}
      {variants.length > 1 && (
        <Stack
          className={"AiChatMessage-hoverAction"}
          direction={"row"}
          spacing={0.25}
          sx={{ ...aiChatMessageStyles.copyAction, ...aiChatMessageStyles.branchActions }}
        >
          <IconButton
            size={"small"}
            disabled={variantIndex <= 0 || isInteractionDisabled}
            onClick={async (event) => {
              event.stopPropagation();
              await selectVariant(variantIndex - 1);
            }}
          >
            <ArrowBackIosNewRounded />
          </IconButton>
          <Typography variant={"caption"}>
            {variantIndex + 1} / {variants.length}
          </Typography>
          <IconButton
            size={"small"}
            disabled={variantIndex >= variants.length - 1 || isInteractionDisabled}
            onClick={async (event) => {
              event.stopPropagation();
              await selectVariant(variantIndex + 1);
            }}
          >
            <ArrowForwardIosRounded />
          </IconButton>
        </Stack>
      )}
      {/*
       only adapted the AiChatMessage-hoverAction thing because im not touching that unreviewed ai slop. this is completely against our coding standards.
       from my brief look at the code the whole component should be refactored
      */}
      <Typography variant={"caption"} sx={{ display: "none" }} className={"AiChatMessage-hoverAction"}>
        {dayjs(message.created).format("DD.MM.YY, HH:mm")} Uhr
      </Typography>
    </Stack>
  );
}
